import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import MarketingLayout from './shared/MarketingLayout';
import { PageHero, Section, SectionHeader, FeatureCard, CTAStrip } from './shared/blocks';
import SEO from './shared/SEO';
import { Button } from '../../components/ui/button';
import { ERP_MODULES } from './content';

export default function SchoolERPPage() {
  return (
    <MarketingLayout>
      <SEO
        title="School ERP Software | School Management System for India"
        description="A complete School ERP — attendance, fees, communication, student records, academics, administration. Built for Indian schools by Indian operations heads. India-hosted. Apply for a 30-day pilot."
        path="/school-erp"
        keywords={['school ERP software', 'school management system', 'student information system', 'attendance management system', 'school ERP India']}
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'School ERP', url: '/school-erp' }]}
      />
      <PageHero
        eyebrow="School ERP · School Management System"
        title="A School ERP your"
        accent="staff will actually use."
        subtitle="Attendance, fees, communication, student records, academics, administration. Designed for Indian schools — built into the same platform your teachers already love."
        testid="school-erp-hero"
      >
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/school-pilot-program" className="w-full sm:w-auto">
            <Button size="lg" className="bg-mk-blue hover:bg-mk-deep text-white h-12 px-7 font-semibold w-full sm:w-auto">
              Apply for 30-Day Pilot <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/contact" className="w-full sm:w-auto">
            <Button size="lg" variant="outline" className="h-12 px-7 font-semibold w-full sm:w-auto border-2">Book a demo</Button>
          </Link>
        </div>
      </PageHero>

      <Section tone="muted">
        <SectionHeader
          eyebrow="Modules"
          title="The six pillars of a working school ERP."
          subtitle="Each module is opinionated about what an Indian school operations team actually needs — and ruthless about what it doesn't."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {ERP_MODULES.map((m) => (
            <div key={m.id} id={m.id}>
              <FeatureCard icon={m.icon} name={m.name} bullets={m.points} tone={m.tone} testid={`school-erp-${m.id}`} />
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeader eyebrow="One platform" title="The whole school. One login." />
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { title: 'Principal / Admin', points: ['School-wide dashboards', 'Defaulter & attendance alerts', 'Staff payroll & leaves'] },
            { title: 'Teachers', points: ['Attendance in <10s per class', 'Generate, share, and grade', 'Parent communication built in'] },
            { title: 'Parents / Students', points: ['Mobile-first portal', 'Fee receipts & history', 'Homework + report cards'] },
          ].map((r) => (
            <div key={r.title} className="rounded-2xl border border-mk-line dark:border-mk-edge p-6 bg-white dark:bg-mk-navy">
              <h3 className="font-bold text-lg">{r.title}</h3>
              <ul className="mt-4 space-y-2 text-sm">
                {r.points.map((p) => (
                  <li key={p} className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-600 mt-0.5" /> {p}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      <CTAStrip
        title="Bring ActanonEdu to your school."
        subtitle="30-day pilot · Founder-led onboarding · custom blueprint match."
        primary={{ label: 'Apply for Pilot', href: '/school-pilot-program' }}
        secondary={{ label: 'Chat with Founder', wa: 'Hi Founder! I want to bring ActanonEdu ERP to my school.' }}
        testid="school-erp-final-cta"
      />
    </MarketingLayout>
  );
}
